import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Server, HardDrive, Router, Cctv, Home, Archive, ShieldCheck, Laptop,
  ArrowRight, Clock3, ChevronRight, ChevronDown, Search, Settings2,
  PanelsTopLeft, Network, Video, House, DatabaseBackup, Cloud, Radio, Route,
  Container, FileText, Shield, BellRing, BatteryCharging, Workflow, Ticket,
  Boxes, Activity, Link2, MonitorSmartphone, Wrench, ExternalLink, RefreshCw,
  KeyRound,
} from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { useMe } from '../shell/AppShell.js'
import { fraicheur } from '../shell/status.js'
import { apiGet } from '../admin/primitives.js'
import { Link, navigate } from '../router.js'
import { HubSupervision, useHubSonde } from '../supervision/vues/hub/HubSupervision.js'
import { SOURCES_CONFIEES, estSourceConfiee } from '../supervision/vues/hub/modele.js'
import { useMenuServiceInfra } from '../layout/menu-tuiles.js'

/**
 * LOT 8.1 — Hub Infrastructure piloté par le CATALOGUE, page « Tous les
 * services », détail générique. Trois niveaux de lecture :
 *   1. « Le club va-t-il bien ? »       — verdict + HERO cards ;
 *   2. « Qu'est-ce qui pose problème ? » — catégories, standard, à traiter ;
 *   3. « Tous les détails »              — services techniques repliés,
 *      Tous les services, drill-down.
 * Le navigateur AFFICHE des états déjà dérivés côté serveur.
 */

/* ─────────────────────────────────────────── types du modèle de lecture ── */

export interface CatalogEntry {
  slug: string
  display_name: string
  description: string | null
  category: string
  category_label: string
  category_kind: 'CORE' | 'BUSINESS' | 'TECHNICAL'
  icon: string | null
  display_order: number
  prominence: 'HERO' | 'STANDARD' | 'COMPACT' | 'HIDDEN'
  criticality: 'CRITICAL' | 'IMPORTANT' | 'NORMAL' | 'AUXILIARY'
  criticality_label: string
  show_in_infrastructure: boolean
  show_in_global_status: boolean
  affects_global_health: boolean
  is_enabled: boolean
  state: 'HEALTHY' | 'DEGRADED' | 'UNAVAILABLE' | 'STALE' | 'UNKNOWN'
  state_label: string
  headline: string | null
  problems: string[]
  metrics: { key: string; label: string; value: number | string; unit: string | null }[]
  seconds_since_success: number | null
  showing_lkg: boolean
  last_error_class: string | null
  components: { key: string; name: string; kind: string; state: 'ONLINE' | 'OFFLINE' | 'WARNING'; detail: string | null; from_source: string; missing?: boolean }[]
  depends_on: string[]
  open_url: string | null
  source_ref: string | null
}

export interface CatalogData {
  generated_at: string
  categories: { key: string; label: string; kind: 'CORE' | 'BUSINESS' | 'TECHNICAL'; display_order: number }[]
  entries: CatalogEntry[]
  summary: {
    heroes_total: number
    heroes_healthy: number
    headline: string
    global_level: 'OPERATIONAL' | 'ATTENTION' | 'DEGRADED'
    attention: { slug: string; label: string; state: string; reason: string; last_error_class?: string | null }[]
    hidden_count: number
  }
}

export const CATALOG_ICONS: Record<string, typeof Server> = {
  PanelsTopLeft, Network, Video, House, Server, DatabaseBackup, ShieldCheck,
  Laptop, Cloud, Radio, Route, Container, FileText, Shield, BellRing,
  BatteryCharging, Workflow, Ticket, Boxes, Activity, Link2,
  MonitorSmartphone, Wrench, HardDrive, Router, Cctv, Home, Archive,
}
export const iconOf = (name: string | null) => CATALOG_ICONS[name ?? ''] ?? Server

export const STATE_DOT: Record<string, string> = {
  HEALTHY: 'lc-dot--ok', DEGRADED: 'lc-dot--warning', STALE: 'lc-dot--warning',
  UNAVAILABLE: 'lc-dot--error', UNKNOWN: '',
}
const STATE_PILL: Record<string, string> = {
  HEALTHY: 'lc-pill--ok', DEGRADED: 'lc-pill--warning', STALE: 'lc-pill--warning',
  UNAVAILABLE: 'lc-pill--error', UNKNOWN: 'lc-pill--idle',
}

/**
 * Source confiée à Sonde que la SUPERVISION ne voit plus (miroir périmé, Sonde
 * injoignable, 21/10/2026) : on ne sait rien de l'équipement, on ne le peint
 * donc jamais en rouge — point neutre, pastille « idle ». Le serveur le dit
 * par la classe d'erreur, ou par le libellé pour les lignes qui n'en portent pas.
 */
export function supervisionMuette(x: { state: string; last_error_class?: string | null; state_label?: string }): boolean {
  // Le serveur publie STALE (ou UNKNOWN sans dernier état connu) ; UNAVAILABLE
  // reste reconnu pour une réponse d'avant ce changement.
  return (x.state === 'UNAVAILABLE' || x.state === 'STALE' || x.state === 'UNKNOWN')
    && (x.last_error_class === 'SUPERVISION_UNAVAILABLE' || x.state_label === 'Supervision indisponible')
}
export const stateDot = (x: { state: string; last_error_class?: string | null; state_label?: string }): string =>
  supervisionMuette(x) ? '' : STATE_DOT[x.state] ?? ''
const statePill = (x: { state: string; last_error_class?: string | null; state_label?: string }): string =>
  supervisionMuette(x) ? 'lc-pill--idle' : STATE_PILL[x.state] ?? ''
const COMPONENT_DOT: Record<string, string> = {
  ONLINE: 'lc-dot--ok', WARNING: 'lc-dot--warning', OFFLINE: 'lc-dot--error',
}
const COMPONENT_LABEL: Record<string, string> = {
  ONLINE: 'En ligne', WARNING: 'À surveiller', OFFLINE: 'Hors ligne',
}

export function useCatalog(intervalMs = 30_000): CatalogData | null {
  const [d, setD] = useState<CatalogData | null>(null)
  const charger = useCallback(() => {
    apiGet<CatalogData>('/api/v1/infrastructure/services')
      .then((r) => { if ('entries' in r) setD(r) }).catch(() => undefined)
  }, [])
  useEffect(() => {
    charger()
    const t = setInterval(charger, intervalMs)
    return () => clearInterval(t)
  }, [charger, intervalMs])
  return d
}

/* ═══════════════════════════════ HUB /infrastructure ═══════════════════ */

/**
 * SUPERVISION (21/10/2026) — /infrastructure devient LE tableau de bord
 * technique : le haut de page est lu dans Sonde (niveau global, salles,
 * attention, incidents, direct, familles, domaines — `HubSupervision`).
 * Plus bas, les services PROPRES À GESTION (billetterie, relais finance,
 * Meta, Main Courante, Parc…) restent lus dans le catalogue de Gestion. Les 9
 * sources techniques confiées à Sonde quittent cette section ; leurs
 * anciennes fiches `/infrastructure/<slug>` restent ouvertes depuis
 * « Sources techniques confiées à Sonde ».
 */
export function InfrastructureHub() {
  const meta = useShellMeta()
  const me = useMe()
  const d = useCatalog()
  const perms = me?.permissions ?? null
  const canManage = perms?.includes('infrastructure.catalog.manage') ?? false
  const voitSonde = perms ? perms.includes('sonde.read') : null
  const voitDetails = perms?.includes('sonde.details.read') ?? false
  const sonde = useHubSonde(voitSonde === true, voitDetails)
  const overview = sonde.ov.lecture?.etat === 'ok' ? sonde.ov.lecture.data : sonde.ov.precedente

  const visible = (d?.entries ?? []).filter((e) => e.is_enabled && e.show_in_infrastructure && e.prominence !== 'HIDDEN' && !estSourceConfiee(e))

  // AFFECTATION EXHAUSTIVE — chaque service visible tombe dans un niveau, et
  // dans un seul. L'ancienne version croisait deux filtres indépendants et
  // laissait un trou : un service STANDARD rangé dans une catégorie TECHNIQUE
  // était refusé au niveau 2 (réservé aux catégories métier) ET au niveau 3
  // (qui excluait STANDARD). Il n'apparaissait alors nulle part — bien que
  // actif, coché « afficher », et remonté par l'API. C'est ce qui rendait
  // StoryScan introuvable sur cette page.
  const niveau = (e: CatalogEntry): 1 | 2 | 3 =>
    e.prominence === 'HERO' ? 1
      : e.category_kind !== 'TECHNICAL' && e.prominence === 'STANDARD' ? 2
        : 3
  const heroes = visible.filter((e) => niveau(e) === 1)
  const businessCats = (d?.categories ?? []).filter((c) => c.kind !== 'TECHNICAL')
  const standardByCat = (key: string) => visible.filter((e) => niveau(e) === 2 && e.category === key)
  const technical = visible.filter((e) => niveau(e) === 3)
  const techHealthy = technical.filter((e) => e.state === 'HEALTHY').length
  const [techOpen, setTechOpen] = useState(false)
  // Les lignes « à traiter » du catalogue qui ne parlent pas d'une source confiée à Sonde.
  const slugsConfies = new Set(SOURCES_CONFIEES.map((s) => s.slug))
  const attentionGestion = (d?.summary.attention ?? []).filter((a) => !slugsConfies.has(a.slug))

  return (
    <Shell active="infrastructure" meta={meta}>
      <main className="lc-page lc-page--large lc-stagger hub--infrastructure sv-hub">
        <HubSupervision donnees={sonde} voit={voitSonde} voitDetails={voitDetails}
          peutIntegrations={perms?.includes('integration.read') ?? false} />

        <section className="sv-hub-gestion" aria-labelledby="sv-hub-gestion-titre">
          <header className="sv-hub-gestion__tete">
            <div className="lc-stack">
              <h2 className="sv-hub-gestion__titre" id="sv-hub-gestion-titre">Services propres à Gestion</h2>
              <p className="lc-hint">Billetterie, relevés, connexion Instagram, Main Courante, Parc… : Gestion les surveille lui-même, comme avant.</p>
            </div>
            <div className="infra-head__actions">
              {d ? (
                <div className={`infra-verdict ${attentionGestion.length === 0 ? 'infra-verdict--ok' : 'infra-verdict--warn'}`}>
                  <span className={`lc-dot ${attentionGestion.length === 0 ? 'lc-dot--ok' : 'lc-dot--warning'}`} aria-hidden="true" />
                  {attentionGestion.length === 0 ? 'Rien à traiter côté Gestion' : `${attentionGestion.length} point${attentionGestion.length > 1 ? 's' : ''} à traiter`}
                </div>
              ) : null}
              {canManage ? (
                <Link className="lc-btn" href="/settings/services"><Settings2 size={14} aria-hidden="true" /> Personnaliser</Link>
              ) : null}
            </div>
          </header>

          {attentionGestion.length > 0 ? (
            <section className="lc-panel infra-attention" aria-label="À traiter">
              {attentionGestion.map((a) => (
                /*
                 * La ligne d'alerte mène à la FICHE SOURCE, pas à la fiche
                 * service. Les deux pages se ressemblent — même titre, même
                 * pastille, mêmes compteurs — mais seule la fiche source porte
                 * le formulaire de remplacement de clé. Envoyer quelqu'un sur le
                 * jumeau, c'est lui montrer un écran qui décrit sa panne sans
                 * lui donner le moyen d'agir : le clic « ne fait rien ».
                 */
                <button key={a.slug} className="infra-attention__row" onClick={() => navigate(`/infrastructure/${a.slug}`)}>
                  <span className={`lc-dot ${supervisionMuette(a) ? '' : STATE_DOT[a.state] ?? 'lc-dot--warning'}`} aria-hidden="true" />
                  <span className="lc-stack">
                    <span className="infra-attention__name">{a.label}</span>
                    <span className="lc-hint">{a.reason}</span>
                  </span>
                  <ChevronRight size={16} aria-hidden="true" />
                </button>
              ))}
            </section>
          ) : null}

          {/* ─── NIVEAU 1 : systèmes principaux ─── */}
          {heroes.length > 0 ? (
            <div className="infra-grid">
              {heroes.map((e) => <HeroCard key={e.slug} e={e} />)}
            </div>
          ) : null}

          {/* ─── NIVEAU 2 : catégories métier (standard) ─── */}
          {businessCats.map((c) => {
            const items = standardByCat(c.key)
            if (items.length === 0) return null
            return (
              <section key={c.key} className="infra-cat">
                <h3 className="infra-section-title">{c.label}</h3>
                <div className="lc-panel infra-rows">
                  {items.map((e) => <StandardRow key={e.slug} e={e} />)}
                </div>
              </section>
            )
          })}

          {/* ─── NIVEAU 3 : services techniques de Gestion (repliables) ─── */}
          {technical.length > 0 ? (
            <section className="infra-cat">
              <button className="infra-section-title infra-section-title--btn" onClick={() => setTechOpen((o) => !o)}
                aria-expanded={techOpen}>
                {techOpen ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
                Autres services
                <span className={`lc-pill ${techHealthy === technical.length ? 'lc-pill--ok' : 'lc-pill--warning'}`}>
                  {techHealthy} / {technical.length}
                </span>
              </button>
              {techOpen ? (
                <div className="lc-panel infra-rows">
                  {technical.map((e) => <StandardRow key={e.slug} e={e} compact />)}
                </div>
              ) : null}
            </section>
          ) : null}

          <SourcesConfiees entries={d?.entries ?? []} proprietaires={overview?.shadow_sources ?? null} />

          <div className="infra-footlinks">
            <Link className="lc-btn" href="/infrastructure/services">
              Voir tous les services{d ? ` (${d.entries.filter((e) => e.is_enabled).length})` : ''}
              <ArrowRight size={14} aria-hidden="true" />
            </Link>
          </div>
          {!d ? <p className="lc-hint">Chargement des services de Gestion…</p> : null}
        </section>
      </main>
    </Shell>
  )
}

/**
 * Les 9 sources techniques confiées à Sonde : leurs anciennes fiches
 * (`/infrastructure/dell`, `/infrastructure/unifi-network`…) gardent
 * l'historique de Gestion et le formulaire de clé. Qui prévient pour chacune
 * vient de Sonde (`shadow_sources[].owner`) ; inconnu = « — ».
 */
function SourcesConfiees({ entries, proprietaires }: {
  entries: CatalogEntry[]
  proprietaires: { source: string; label: string; owner: 'gestion' | 'sonde' | null }[] | null
}) {
  const [ouvert, setOuvert] = useState(false)
  return (
    <section className="infra-cat sv-hub-sources">
      <button className="infra-section-title infra-section-title--btn" onClick={() => setOuvert((o) => !o)} aria-expanded={ouvert}>
        {ouvert ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
        Sources techniques confiées à Sonde
        <span className="lc-pill lc-pill--idle">{SOURCES_CONFIEES.length}</span>
      </button>
      {ouvert ? (
        <div className="lc-panel infra-rows">
          {SOURCES_CONFIEES.map((s) => {
            const e = entries.find((x) => x.source_ref === s.code) ?? null
            const p = proprietaires?.find((x) => x.source === s.code) ?? null
            return (
              <Link key={s.code} className="sv-hub-sources__ligne" href={`/infrastructure/${s.slug}`}>
                <span className="lc-stack">
                  <span className="sv-hub-sources__nom">{e?.display_name ?? s.nom}</span>
                  {p ? <span className="lc-hint">{p.label}</span> : null}
                </span>
                <span className="sv-hub-sources__etat">
                  {p?.owner === 'sonde' ? 'Sonde surveille' : p?.owner === 'gestion' ? 'Gestion surveille' : '—'}
                </span>
                {e ? (
                  <span className="sv-hub-sources__etat">
                    <span className={`lc-dot ${stateDot(e)}`} aria-hidden="true" />
                    {e.state_label}
                  </span>
                ) : <span className="sv-hub-sources__etat">—</span>}
              </Link>
            )
          })}
        </div>
      ) : null}
    </section>
  )
}

function HeroCard({ e }: { e: CatalogEntry }) {
  const Icon = iconOf(e.icon)
  const f = fraicheur(e.seconds_since_success, e.showing_lkg || e.state === 'STALE')
  const menu = useMenuServiceInfra()
  return (
    <Link href={`/infrastructure/services/${e.slug}`} className={`infra-card infra-card--hero infra-card--${supervisionMuette(e) ? 'unknown' : e.state.toLowerCase()}`}
      {...menu(e.slug, e.display_name)}>
      <div className="infra-card__head">
        <span className="infra-card__icon"><Icon size={19} aria-hidden="true" /></span>
        <span className="infra-card__name">{e.display_name}</span>
      </div>
      <div className="infra-card__state">
        <span className={`lc-dot ${stateDot(e)}`} aria-hidden="true" />
        {e.state_label}
      </div>
      <div className="infra-card__body lc-stack">
        {e.state === 'UNKNOWN'
          ? <span className="lc-hint">Aucune collecte n’a encore abouti.</span>
          : (
            <>
              {e.headline ? <span>{e.headline}</span> : null}
              {e.showing_lkg ? <span className="lc-hint">Source injoignable — dernières données connues.</span> : null}
              {e.problems.filter((p) => p !== e.headline).slice(0, 2).map((p, i) => (
                <span key={i} className="lc-hint infra-card__problem">{p}</span>
              ))}
            </>
          )}
      </div>
      <div className="infra-card__foot">
        <span className="lc-hint infra-card__fresh"><Clock3 size={12} aria-hidden="true" /> {f ?? 'Jamais collecté'}</span>
        <span className="infra-card__cta">Voir les détails <ArrowRight size={13} aria-hidden="true" /></span>
      </div>
    </Link>
  )
}

function StandardRow({ e, compact }: { e: CatalogEntry; compact?: boolean }) {
  const Icon = iconOf(e.icon)
  const f = fraicheur(e.seconds_since_success, e.showing_lkg || e.state === 'STALE')
  const menu = useMenuServiceInfra()
  return (
    <Link href={`/infrastructure/services/${e.slug}`} className={`infra-row ${compact ? 'infra-row--compact' : ''}`}
      {...menu(e.slug, e.display_name)}>
      <span className="infra-row__icon"><Icon size={compact ? 15 : 17} aria-hidden="true" /></span>
      <span className="lc-stack infra-row__main">
        <span className="infra-row__name">{e.display_name}</span>
        {!compact && e.headline ? <span className="lc-hint">{e.headline}</span> : null}
        {e.problems.length > 0 && e.state !== 'HEALTHY'
          ? <span className="lc-hint infra-card__problem">{e.problems[0]}</span> : null}
      </span>
      <span className="infra-row__state">
        <span className={`lc-dot ${stateDot(e)}`} aria-hidden="true" />
        {e.state_label}
      </span>
      {!compact ? <span className="lc-hint infra-row__fresh">{f ?? ''}</span> : null}
      <ChevronRight size={15} aria-hidden="true" className="infra-row__chev" />
    </Link>
  )
}

/* ═══════════════════════ /infrastructure/services (tous) ═══════════════ */

export function AllServicesPage() {
  const meta = useShellMeta()
  const d = useCatalog()
  const [q, setQ] = useState('')
  const [filtre, setFiltre] = useState<'all' | 'problems' | 'hidden'>('all')
  const [cat, setCat] = useState<string>('')

  const rows = useMemo(() => {
    let list = (d?.entries ?? []).filter((e) => e.is_enabled || filtre === 'hidden')
    if (filtre === 'problems') list = list.filter((e) => e.state !== 'HEALTHY')
    if (filtre === 'hidden') list = list.filter((e) => e.prominence === 'HIDDEN' || !e.is_enabled)
    if (cat) list = list.filter((e) => e.category === cat)
    const n = q.trim().toLowerCase()
    if (n) list = list.filter((e) => e.display_name.toLowerCase().includes(n) || (e.description ?? '').toLowerCase().includes(n))
    return list
  }, [d, q, filtre, cat])

  return (
    <Shell active="infrastructure" meta={meta}>
      <main className="lc-page lc-stagger">
        <Link className="lc-crumb" href="/infrastructure">← Infrastructure</Link>
        <h1 className="lc-title">Tous les services</h1>
        <p className="lc-subtitle">Le catalogue complet — y compris les services masqués, toujours supervisés.</p>

        <div className="infra-toolbar">
          <div className="lc-seg" role="tablist">
            {([['all', 'Tous'], ['problems', 'Problèmes'], ['hidden', 'Masqués']] as const).map(([k, l]) => (
              <button key={k} role="tab" aria-selected={filtre === k}
                className={`lc-seg__btn ${filtre === k ? 'lc-seg__btn--on' : ''}`}
                onClick={() => setFiltre(k)}>{l}</button>
            ))}
          </div>
          <select className="infra-select" value={cat} onChange={(e) => setCat(e.target.value)} aria-label="Filtrer par catégorie">
            <option value="">Toutes les catégories</option>
            {(d?.categories ?? []).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <span className="lc-panel__spacer" />
          <label className="rh-search">
            <Search size={15} aria-hidden="true" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" aria-label="Rechercher un service" />
          </label>
        </div>

        <section className="lc-panel gd-panel">
          <table className="lc-table">
            <thead><tr><th>Service</th><th>Catégorie</th><th>Criticité</th><th>Affichage</th><th>État</th></tr></thead>
            <tbody>
              {rows.map((e) => {
                const Icon = iconOf(e.icon)
                return (
                  <tr key={e.slug} className="infra-allrow" onClick={() => navigate(`/infrastructure/services/${e.slug}`)}>
                    <td>
                      <span className="rh-person">
                        <span className="infra-row__icon" aria-hidden="true"><Icon size={15} /></span>
                        <span className="lc-stack">
                          <span>{e.display_name}</span>
                          {e.description ? <span className="lc-hint">{e.description}</span> : null}
                        </span>
                      </span>
                    </td>
                    <td className="lc-hint">{e.category_label}</td>
                    <td className="lc-hint">{e.criticality_label}</td>
                    <td className="lc-hint">
                      {e.prominence === 'HERO' ? 'Principal' : e.prominence === 'STANDARD' ? 'Standard' : e.prominence === 'COMPACT' ? 'Compact' : 'Masqué'}
                      {!e.is_enabled ? ' · désactivé' : ''}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <span className={`lc-dot ${stateDot(e)}`} aria-hidden="true" style={{ marginRight: 8, display: 'inline-block' }} />
                      {e.state_label}
                    </td>
                  </tr>
                )
              })}
              {d && rows.length === 0 ? (
                <tr><td colSpan={5} className="lc-hint" style={{ padding: 'var(--sp-5)' }}>Aucun service ne correspond.</td></tr>
              ) : null}
            </tbody>
          </table>
        </section>
      </main>
    </Shell>
  )
}

/* ═══════════════════ /infrastructure/services/:slug (détail) ═══════════ */

interface ServiceDetail {
  entry: CatalogEntry
  depends_on: { slug: string; label: string; state: string; state_label: string }[]
  used_by: { slug: string; label: string; state: string; state_label: string }[]
  metric_catalog: { key: string; label: string; unit: string | null; type: string }[]
  changes: { component: string | null; component_name: string | null; from_state: string; to_state: string; detail: string | null; changed_at: string }[]
  active_alerts: { severity: string; title: string; occurred_at: string | null }[]
  refresh_interval_s: number | null
  consecutive_failures: number
  last_error: string | null
  can_technical_details: boolean
}

const SOURCE_SLUGS: Record<string, string> = {
  GESTION: 'gestion', DELL: 'dell', UNIFI_NETWORK: 'unifi-network',
  UNIFI_PROTECT: 'unifi-protect', HOME_ASSISTANT: 'home-assistant', BACKUPS: 'backups',
  MEDIAMTX: 'mediamtx', NPM: 'nginx-proxy-manager', DOCKER: 'docker', ENDPOINTS: 'acces-publics',
}

function etatCourt(s: string): string {
  const map: Record<string, string> = {
    HEALTHY: 'Opérationnel', DEGRADED: 'Dégradé', UNAVAILABLE: 'Indisponible',
    STALE: 'Données anciennes', UNKNOWN: 'Inconnu',
    ONLINE: 'En ligne', OFFLINE: 'Hors ligne', WARNING: 'À surveiller',
  }
  return map[s] ?? s
}

function quand(iso: string): string {
  const dt = new Date(iso)
  const auj = new Date().toDateString() === dt.toDateString()
  const h = dt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' })
  return auj ? h : `${dt.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', timeZone: 'Europe/Paris' })} ${h}`
}

export function InfraServicePage({ slug }: { slug: string }) {
  const meta = useShellMeta()
  const me = useMe()
  const [d, setD] = useState<ServiceDetail | null>(null)
  const [absent, setAbsent] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const charger = useCallback(() => {
    apiGet<ServiceDetail>(`/api/v1/infrastructure/services/${slug}`)
      .then((r) => { if ('entry' in r) setD(r); else setAbsent(true) })
      .catch(() => setAbsent(true))
  }, [slug])
  useEffect(() => {
    charger()
    const t = setInterval(charger, 30_000)
    return () => clearInterval(t)
  }, [charger])

  const e = d?.entry
  const Icon = iconOf(e?.icon ?? null)
  const f = e ? fraicheur(e.seconds_since_success, e.showing_lkg || e.state === 'STALE') : null
  const canRefresh = (me?.permissions.includes('sync.run') ?? false) && !!e?.source_ref && /^[A-Z_]+$/.test(e.source_ref)

  const refresh = async () => {
    if (!e?.source_ref) return
    setRefreshing(true)
    try {
      const session = await fetch('/api/v1/auth/session', { credentials: 'same-origin' }).then((r) => r.json()) as { csrf_token?: string }
      await fetch(`/api/v1/infrastructure/${SOURCE_SLUGS[e.source_ref] ?? e.slug}/refresh`, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'x-csrf-token': session.csrf_token ?? '' },
      })
      setTimeout(() => { charger(); setRefreshing(false) }, 4000)
    } catch { setRefreshing(false) }
  }

  return (
    <Shell active="infrastructure" meta={meta}>
      <main className="lc-page lc-stagger">
        <Link className="lc-crumb" href="/infrastructure">← Infrastructure</Link>
        {absent ? <p className="lc-hint" style={{ marginTop: 'var(--sp-5)' }}>Service inconnu.</p> : null}
        {!e && !absent ? <p className="lc-hint" style={{ marginTop: 'var(--sp-5)' }}>Chargement…</p> : null}
        {e ? (
          <>
            <header className="infra-detail-head">
              <span className="infra-card__icon infra-card__icon--lg"><Icon size={24} aria-hidden="true" /></span>
              <div className="lc-stack">
                <h1 className="lc-title" style={{ margin: 0 }}>{e.display_name}</h1>
                <span className="lc-hint">{e.description ?? e.category_label}{e.criticality !== 'NORMAL' ? ` · ${e.criticality_label}` : ''}</span>
              </div>
              <span className={`lc-pill ${statePill(e)}`}>{e.state_label}</span>
              <span className="lc-panel__spacer" />
              <span className="lc-hint infra-card__fresh"><Clock3 size={13} aria-hidden="true" /> {f ?? 'Jamais collecté'}</span>
              {e.open_url ? (
                <a className="lc-btn" href={e.open_url} target="_blank" rel="noopener noreferrer">
                  Ouvrir le service <ExternalLink size={13} aria-hidden="true" />
                </a>
              ) : null}
              {canRefresh ? (
                <button className="lc-btn" onClick={refresh} disabled={refreshing}>
                  <RefreshCw size={14} aria-hidden="true" className={refreshing ? 'infra-spin' : undefined} /> Collecter maintenant
                </button>
              ) : null}
              {/*
                * Une clé refusée se REMPLACE — mais le formulaire vit sur la
                * fiche source. Plutôt qu'une phrase invitant à « ouvrir le
                * détail » depuis un écran qui ressemble déjà au détail, on
                * pose le chemin, en toutes lettres et cliquable.
                */}
              {e.last_error_class === 'AUTH' && e.source_ref && SOURCE_SLUGS[e.source_ref] ? (
                <Link className="lc-btn lc-btn--primary" href={`/infrastructure/${SOURCE_SLUGS[e.source_ref]}`}>
                  <KeyRound size={14} aria-hidden="true" /> Régler l’intégration
                </Link>
              ) : null}
            </header>

            {e.showing_lkg ? (
              <div className="adm-note" role="status">
                Cette source ne répond plus — Gestion affiche les dernières données fiables, sans les effacer.
              </div>
            ) : null}

            {e.headline || e.problems.length > 0 ? (
              <section className="lc-panel gd-panel infra-headline">
                <div className="lc-panel__body lc-stack">
                  {e.headline ? <span className="infra-headline__main">{e.headline}</span> : null}
                  {e.problems.filter((p) => p !== e.headline).map((p, i) => (
                    <span key={i} className="lc-hint">⚠ {p}</span>
                  ))}
                </div>
              </section>
            ) : null}

            {e.metrics.length > 0 ? (
              <section className="infra-metrics">
                {e.metrics.map((m) => (
                  <div key={m.key} className="infra-metric">
                    <span className="infra-metric__value">{m.value}{m.unit === '%' ? ' %' : ''}</span>
                    <span className="lc-hint">{m.label}{m.unit && m.unit !== '%' ? ` (${m.unit})` : ''}</span>
                  </div>
                ))}
              </section>
            ) : null}

            {e.components.length > 0 ? (
              <section className="lc-panel gd-panel">
                <header className="lc-panel__head"><h2 className="lc-section-title">Composants</h2></header>
                <table className="lc-table">
                  <thead><tr><th>Composant</th><th>État</th><th>Détail</th></tr></thead>
                  <tbody>
                    {e.components.map((c) => (
                      <tr key={`${c.from_source}:${c.key}`}>
                        <td>{c.name}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <span className={`lc-dot ${COMPONENT_DOT[c.state] ?? ''}`} aria-hidden="true" style={{ marginRight: 8, display: 'inline-block' }} />
                          {COMPONENT_LABEL[c.state] ?? c.state}
                        </td>
                        <td className="lc-hint">{c.detail ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            ) : null}

            {(d!.depends_on.length > 0 || d!.used_by.length > 0) ? (
              <section className="lc-panel gd-panel">
                <header className="lc-panel__head"><h2 className="lc-section-title">Relations</h2></header>
                <div className="lc-panel__body infra-rels">
                  {d!.depends_on.length > 0 ? (
                    <div className="lc-stack">
                      <span className="infra-rels__title">Dépend de</span>
                      {d!.depends_on.map((r) => (
                        <Link key={r.slug} className="infra-rels__row" href={`/infrastructure/services/${r.slug}`}>
                          <span className={`lc-dot ${stateDot(r)}`} aria-hidden="true" /> {r.label}
                          <span className="lc-hint"> · {r.state_label}</span>
                        </Link>
                      ))}
                    </div>
                  ) : null}
                  {d!.used_by.length > 0 ? (
                    <div className="lc-stack">
                      <span className="infra-rels__title">Utilisé par</span>
                      {d!.used_by.map((r) => (
                        <Link key={r.slug} className="infra-rels__row" href={`/infrastructure/services/${r.slug}`}>
                          <span className={`lc-dot ${stateDot(r)}`} aria-hidden="true" /> {r.label}
                          <span className="lc-hint"> · {r.state_label}</span>
                        </Link>
                      ))}
                    </div>
                  ) : null}
                </div>
              </section>
            ) : null}

            {(d!.active_alerts.length ?? 0) > 0 ? (
              <section className="lc-panel gd-panel">
                <header className="lc-panel__head"><h2 className="lc-section-title">Alertes actives</h2></header>
                <div className="lc-panel__body lc-stack">
                  {d!.active_alerts.map((a, i) => (
                    <span key={i}><span className={`lc-dot ${a.severity === 'CRITICAL' ? 'lc-dot--error' : 'lc-dot--warning'}`} aria-hidden="true" style={{ marginRight: 8, display: 'inline-block' }} />{a.title}</span>
                  ))}
                </div>
              </section>
            ) : null}

            {d!.changes.length > 0 ? (
              <section className="lc-panel gd-panel">
                <header className="lc-panel__head"><h2 className="lc-section-title">Historique récent</h2></header>
                <table className="lc-table">
                  <thead><tr><th>Quand</th><th>Quoi</th><th>Changement</th></tr></thead>
                  <tbody>
                    {d!.changes.map((c, i) => (
                      <tr key={i}>
                        <td className="lc-hint" style={{ whiteSpace: 'nowrap' }}>{quand(c.changed_at)}</td>
                        <td>{c.component_name ?? e.display_name}</td>
                        <td>
                          <span className="lc-stack">
                            <span>{etatCourt(c.from_state)} → {etatCourt(c.to_state)}</span>
                            {c.detail ? <span className="lc-hint">{c.detail}</span> : null}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            ) : null}

            {d!.metric_catalog.length > 0 ? (
              <section className="lc-panel gd-panel">
                <header className="lc-panel__head"><h2 className="lc-section-title">Données disponibles</h2></header>
                <div className="lc-panel__body">
                  <p className="lc-hint" style={{ marginBottom: 'var(--sp-3)' }}>
                    Les mesures que ce service expose — la base du futur moteur d’alertes.
                  </p>
                  <div className="ad-capabilities">
                    {d!.metric_catalog.map((m) => (
                      <span key={m.key}><em>{m.label}</em>{m.unit ? ` (${m.unit})` : ''}</span>
                    ))}
                  </div>
                </div>
              </section>
            ) : null}

            {d!.can_technical_details && d!.last_error ? (
              <section className="lc-panel gd-panel">
                <header className="lc-panel__head"><h2 className="lc-section-title">Détails techniques</h2></header>
                <div className="lc-panel__body lc-stack">
                  <span className="lc-hint">Dernière erreur de collecte ({d!.consecutive_failures} échec{d!.consecutive_failures > 1 ? 's' : ''} consécutif{d!.consecutive_failures > 1 ? 's' : ''}) :</span>
                  <code className="infra-error">{d!.last_error}</code>
                </div>
              </section>
            ) : null}
          </>
        ) : null}
      </main>
    </Shell>
  )
}
